import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useVoiceAgentConversation } from "@/hooks/voice/useVoiceAgentConversation";
import { trackEventOnce } from "@/lib/ga4";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { replaceAttachment } from "@/types/voice/genie-attachment";
import { AgentConfig } from "@/types/agent/agent";
import { AgentPageProps } from "@/types/agent/agent-page";
import type { TranscriptMessage } from "@/pages/agent-interfaces/live-agent/components/TranscriptPanel";

const INSTALL_BANNER_AFTER_CONVERSATION_DELAY_MS = 2500;
const DEFAULT_AGENT_VOICE_VOLUME = 0.85;

type ImageAttachment = {
  id: string;
  file: File;
  previewUrl: string;
  status: "analyzing" | "ready" | "error";
  analysisText?: string;
  compressedBase64?: string;
};

interface UseStudioConversationCoreParams {
  agent: AgentPageProps["agentPageData"] | null;
  propAgentData: AgentPageProps["agentPageData"] | undefined;
  demoMode: AgentPageProps["demoMode"];
  isEmbedMode: boolean;
  isMobile: boolean;
  navigate: ReturnType<typeof import("react-router-dom").useNavigate>;
  userProfile: { id?: string; role?: string; email?: string } | null | undefined;
  memoryConsentMutation: {
    isPending: boolean;
    mutateAsync: (args: { genieId: string; enabled: boolean }) => Promise<{ memoryConsent: string }>;
  };
  canManageCustomerMemory: boolean;
  updateCustomerMemoryConsent: (enabled: boolean) => Promise<void>;
  dismissWelcome: (genieId?: string) => void;
  // Image-attachment plumbing comes from useStudioImageAttachments, which
  // itself needs this hook's voiceAgent (conversationId + endConversation) —
  // so the page calls it AFTER this hook and threads these through as refs,
  // kept fresh every render, rather than as directly-passed values.
  attachmentsRef: React.MutableRefObject<ImageAttachment[]>;
  stageImagesRef: React.MutableRefObject<
    (
      images: Array<{ id: string; file: File; analysisText?: string; compressedBase64?: string }>,
      conversationId: string | null,
    ) => void
  >;
  removeAttachmentRef: React.MutableRefObject<(id: string) => void>;
  setSubmittedImageCountRef: React.MutableRefObject<(updater: (count: number) => number) => void>;
  endConversationAfterUploadsRef: React.MutableRefObject<() => Promise<void>>;
  voiceTroubleSignals: {
    registerConversationEnd: (durationMs: number | null, hadAgentReply: boolean) => void;
    autoOfferToken: number;
    reset: () => void;
  };
  setShowVoiceTrouble: (v: boolean) => void;
  hasVoiceTrouble: boolean;
  setHasVoiceTrouble: (v: boolean) => void;
  showAfterConversationOnce: () => void;
  setShowWrapUp: (v: boolean) => void;
  handoffInProgressRef: React.MutableRefObject<boolean>;
  pendingRedirectUrlRef: React.MutableRefObject<string | null>;
  handleRedirect: (url: string) => void;
  setRedirectMessage: (message: string) => void;
  setIsRedirecting: (v: boolean) => void;
}

/**
 * Owns the voice-agent conversation connection, message/timer/mute state, and
 * the handlers that drive them. Human-handoff orchestration stays in the page
 * (Stage 8) — this hook only touches the handoff refs/setters it needs to keep
 * `onRedirect`/`onConversationEnd` behaving exactly as before.
 */
export function useStudioConversationCore({
  agent,
  propAgentData,
  demoMode,
  isEmbedMode,
  isMobile: _isMobile,
  navigate,
  userProfile,
  memoryConsentMutation,
  canManageCustomerMemory,
  updateCustomerMemoryConsent,
  dismissWelcome,
  attachmentsRef,
  stageImagesRef,
  removeAttachmentRef,
  setSubmittedImageCountRef,
  endConversationAfterUploadsRef,
  voiceTroubleSignals,
  setShowVoiceTrouble,
  hasVoiceTrouble,
  setHasVoiceTrouble,
  showAfterConversationOnce,
  setShowWrapUp,
  handoffInProgressRef,
  pendingRedirectUrlRef,
  handleRedirect,
  setRedirectMessage,
  setIsRedirecting,
}: UseStudioConversationCoreParams) {
  const [lastConversationId, setLastConversationId] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(isEmbedMode);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [conversationStartTime, setConversationStartTime] = useState<number | null>(null);
  const [inlineMessage, setInlineMessage] = useState("");
  const [isTextOnlyMode, setIsTextOnlyMode] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isAgentVoiceMuted, setIsAgentVoiceMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const prePauseMicMutedRef = useRef(false);
  const prePauseAgentMutedRef = useRef(false);
  const pauseActivityIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [startedFromText, setStartedFromText] = useState(false);
  // A deliberate Stop (or a text-initiated session ending) should never count
  // as a "voice trouble" signal — only set right before a user-initiated end.
  const expectedEndRef = useRef(false);
  // True once the current session started from text (welcome "start text" or
  // the composer's first send) — those sessions never had a voice connection
  // to "have trouble" with, so their end shouldn't count as a trouble signal.
  const isTextInitiatedRef = useRef(false);
  // Attachments currently being delivered, so auto-delivery and a manual send
  // never hand the same image to the Genie twice.
  const deliveringIdsRef = useRef<Set<string>>(new Set());
  // Photo-only prompts already shown as the image bubble — their echoed transcript is skipped.
  const attachmentPromptEchoesRef = useRef<string[]>([]);
  // Guards against a second avatar tap starting a duplicate connection while
  // the first startConversation() call is still in flight.
  const voiceStartPendingRef = useRef(false);

  const isAllAudioMuted = useMemo(() => isMicMuted && isAgentVoiceMuted, [isMicMuted, isAgentVoiceMuted]);

  // Keep a ref to demoMode so voice hook callbacks always see the latest without causing re-renders
  const demoModeRef = useRef(demoMode);
  demoModeRef.current = demoMode;

  // Live refs so the voice-hook callbacks (created once) always read current values.
  const agentRef = useRef(agent);
  agentRef.current = agent;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const conversationStartTimeRef = useRef(conversationStartTime);
  conversationStartTimeRef.current = conversationStartTime;

  // The magical wrap-up ends conversations on the standalone live-agent page.
  // Configured redirects, the embed popup, and demo cards keep their own exit
  // rules.
  //
  // Deliberately a callable function, not a memoized boolean: it re-reads
  // agentRef.current fresh each call so async callbacks (onConversationEnd,
  // the handoff-end effect) never see a stale snapshot of the agent.
  const shouldUseWrapUp = () => {
    const a = agentRef.current;
    return !isEmbedMode && !demoMode && !a?.redirect_url;
  };

  // Stable reference for demo conversation overrides — prevents voice agent hook from resetting
  const demoConversationOverrides = useMemo(
    () => (demoMode ? { turn: { turn_timeout: demoMode.maxDurationSeconds * 1000 } } : undefined),
    [demoMode?.maxDurationSeconds],
  );

  const voiceAgent = useVoiceAgentConversation(agent?.elevenlabs_id, {
    welcomeMessage: agent?.welcome_message || agent?.page?.welcome_message || "Click to start",
    agentData: propAgentData || agent,
    ...(agent?.userMemory && { userMemory: agent.userMemory }),
    ...(demoConversationOverrides && {
      conversationOverrides: demoConversationOverrides,
    }),
    onRedirect: (redirectUrl) => {
      // Deferred until the human call ends when this end-of-conversation was
      // caused by a handoff — see the handoffInProgressRef effect in the page.
      if (handoffInProgressRef.current) {
        pendingRedirectUrlRef.current = redirectUrl;
        return;
      }
      handleRedirect(redirectUrl);
    },
    onShowAnalysisLoading: () => {
      setRedirectMessage("Analyzing your conversation...");
      setIsRedirecting(true);
    },
    onConversationEnd: (conversationId) => {
      // Cheap frontend heuristic: a session that ended fast with no reply is a
      // failed connection attempt. Two in a row auto-offers the trouble sheet.
      // Skip the signal for a deliberate Stop or a text-initiated session —
      // neither indicates a voice connection actually struggling.
      const durationMs = conversationStartTimeRef.current
        ? Date.now() - conversationStartTimeRef.current
        : null;
      const hadAgentReply = messagesRef.current.some((m) => !m.isUser);
      if (!expectedEndRef.current && !isTextInitiatedRef.current) {
        voiceTroubleSignals.registerConversationEnd(durationMs, hadAgentReply);
      }
      expectedEndRef.current = false;

      setLastConversationId(conversationId);
      // Keep transcript visible and preserve messages after end
      setShowTranscript(true);
      setConversationStartTime(null);
      // When this end was caused by a human handoff, defer the wrap-up until
      // the human call itself ends instead of poofing it up right as the
      // Twilio call is starting.
      if (handoffInProgressRef.current) {
        // no-op: handoffInProgressRef effect in the page owns the wrap-up trigger
      } else if (shouldUseWrapUp() && messagesRef.current.length > 0) {
        // Open the wrap-up panel once the session and outstanding saves finish.
        setShowWrapUp(true);
      } else if (!isEmbedMode) {
        // Skip the install-app banner inside the embedded widget — there's no
        // room, and a website visitor isn't an "install the app" audience anyway.
        window.setTimeout(() => {
          showAfterConversationOnce();
        }, INSTALL_BANNER_AFTER_CONVERSATION_DELAY_MS);
      }
      demoModeRef.current?.onConversationEnd?.();
    },
  });

  // Fire demo onConversationStart when connected
  useEffect(() => {
    if (voiceAgent.connected) {
      demoModeRef.current?.onConversationStart?.();
    }
  }, [voiceAgent.connected]);

  // Check if agent has user_transcript enabled in client_events
  const isTranscriptEnabled = useCallback(() => {
    const agentConfig = agent?.settings as AgentConfig;
    const clientEvents = agentConfig?.conversation_config?.conversation?.client_events;
    return clientEvents && clientEvents.includes("user_transcript");
  }, [agent]);

  // Auto-open the chat/transcript panel the moment voice connects so users
  // can both speak and see the transcript without an extra click. Text-only
  // starts already open the panel in handleInlineSend, so we skip there.
  // In the embedded widget we open even when the agent didn't enable user
  // transcripts in client_events — the popup is one surface, hiding the chat
  // would force users back to "voice only" which defeats the type-or-talk UX.
  useEffect(() => {
    if (voiceAgent.connected && !startedFromText && (isTranscriptEnabled() || isEmbedMode)) {
      setShowTranscript(true);
    }
  }, [voiceAgent.connected, startedFromText, isEmbedMode, isTranscriptEnabled]);

  // Reset text-only state when conversation disconnects; reset split audio UI state
  useEffect(() => {
    if (!voiceAgent.connected) {
      if (pauseActivityIntervalRef.current) {
        clearInterval(pauseActivityIntervalRef.current);
        pauseActivityIntervalRef.current = null;
      }
      setIsTextOnlyMode(false);
      setStartedFromText(false);
      setIsMicMuted(false);
      setIsAgentVoiceMuted(false);
      setIsPaused(false);
      setChatExpanded(false);
      isTextInitiatedRef.current = false;
    }
  }, [voiceAgent.connected]);

  // Fire GA4 demo_started once per session when the Genie demo page successfully resolves an agent.
  useEffect(() => {
    if (!agent) return;
    const genieId = agent.agents?.id ?? agent.id ?? "";
    const urlName = agent.url_name ?? "";
    trackEventOnce(`demo_started:${urlName || genieId}`, "demo_started", {
      genie_id: genieId,
      url_name: urlName,
      surface: isEmbedMode ? "embed" : "live_agent",
    });
  }, [agent?.id, isEmbedMode]);

  // Setup message listening and transcript storage
  useEffect(() => {
    const service = voiceAgent.getService();
    if (service) {
      const append = (newMessage: TranscriptMessage) => {
        setMessages((prev) => [...prev, newMessage]);

        // Store transcript in localStorage if we have a conversation ID
        const conversationId = voiceAgent.getConversationId();
        if (conversationId) {
          TranscriptStorageService.addMessage(conversationId, newMessage, conversationStartTime || undefined);
        }
      };

      service.onMessage((message, isUser) => {
        if (isUser) {
          const promptIndex = attachmentPromptEchoesRef.current.indexOf(message);
          if (promptIndex !== -1) {
            attachmentPromptEchoesRef.current.splice(promptIndex, 1);
            return;
          }
        }
        append({ message, isUser, timestamp: new Date() });
      });
      service.onAttachment((attachment, replacesUrl) => {
        if (!replacesUrl) {
          append({ message: "", isUser: false, timestamp: new Date(), attachment });
          return;
        }
        setMessages((prev) => replaceAttachment(prev, replacesUrl, attachment));
        const conversationId = voiceAgent.getConversationId();
        if (conversationId) TranscriptStorageService.replaceAttachment(conversationId, replacesUrl, attachment);
      });
    }
  }, [voiceAgent, conversationStartTime]);

  const handleAvatarClick = async () => {
    if (voiceAgent.isConnecting || voiceStartPendingRef.current) return;
    // Track conversation start time and clear previous transcript when starting
    if (!voiceAgent.connected && !voiceAgent.isConnecting) {
      setConversationStartTime(Date.now());
      setMessages([]);
      setHasVoiceTrouble(false);
      expectedEndRef.current = false;
    } else if (voiceAgent.connected) {
      // A deliberate Stop — the resulting conversation-end is expected and
      // should never register as a "voice trouble" signal.
      expectedEndRef.current = true;
      // If ending conversation and agent has redirect URL, show analyzing screen immediately
      if (agent?.redirect_url) {
        setRedirectMessage("Redirecting...");
        setIsRedirecting(true);
      }
    }

    if (voiceAgent.connected) {
      await endConversationAfterUploadsRef.current();
      return;
    }
    voiceStartPendingRef.current = true;
    try {
      const started = await voiceAgent.startConversation();
      if (!started) {
        setHasVoiceTrouble(true);
        setConversationStartTime(null);
      }
    } finally {
      voiceStartPendingRef.current = false;
    }
  };

  const handleStartOver = () => {
    setMessages([]);
    setShowTranscript(false);
    setConversationStartTime(null);
  };

  /**
   * Send the composer's contents — or `override`, when a caller supplies the
   * text itself (a seeded first question from a Genie Page). The override path
   * exists because the seeded question never passes through `inlineMessage`,
   * so there is nothing in state to read.
   */
  async function handleInlineSend(override?: string) {
    const trimmed = (override ?? inlineMessage).trim();
    const sentAttachments = attachmentsRef.current.filter((attachment) => !deliveringIdsRef.current.has(attachment.id));
    if (!trimmed && sentAttachments.length === 0) {
      return;
    }
    // Claimed before the session starts, so auto-delivery can't take them once it connects.
    sentAttachments.forEach((attachment) => deliveringIdsRef.current.add(attachment.id));

    // Ensure conversation is active; when starting from text, prefer text-only mode
    if (!voiceAgent.connected && !voiceAgent.isConnecting) {
      // Set overrides before starting conversation
      const service = voiceAgent.getService();
      if (service) {
        service.setOverrides({
          conversation: { textOnly: true },
          turn: {
            turn_timeout: 600,
            silence_end_call_timeout: 600,
          },
        });
      }
      try {
        await voiceAgent.startConversation();
      } catch (error) {
        console.error("Error starting conversation:", error);
        sentAttachments.forEach((attachment) => deliveringIdsRef.current.delete(attachment.id));
        return;
      }
      setIsTextOnlyMode(true);
      setStartedFromText(true);
      setShowTranscript(true);
      isTextInitiatedRef.current = true;
      // Clear old transcript when starting a new conversation from text
      setMessages([]);
      // Skip mic access grant step for text-only mode
      // Do NOT mute the mic - user can speak later if they want
    } else if (voiceAgent.connected && !isTextOnlyMode && !isMicMuted) {
      // If conversation is already active in voice mode, switch to text overlay
      // Do NOT mute the mic - user can still speak
      setIsTextOnlyMode(true);
      isTextInitiatedRef.current = true;
      setShowTranscript(true);
    }

    await deliverAttachments(sentAttachments, !!trimmed);

    if (trimmed) {
      const userMessage = { message: trimmed, isUser: true, timestamp: new Date() };
      setMessages((prev) => [...prev, userMessage]);
      voiceAgent.sendUserMessage(trimmed);
    }

    setInlineMessage("");
  }

  /**
   * Hand attachments to a connected conversation: the analysis goes in as
   * context, the images render in the transcript and are staged for upload.
   * Without typed text alongside, a short user message is sent too — context
   * alone never prompts a reply, so the Genie would otherwise sit silent.
   */
  async function deliverAttachments(sentAttachments: ImageAttachment[], hasTypedText: boolean) {
    if (sentAttachments.length === 0) return;

    const analysisContext = sentAttachments
      .filter((attachment) => attachment.status === "ready" && attachment.analysisText?.trim())
      .map((attachment) => attachment.analysisText!.trim())
      .join("\n\n");

    if (analysisContext) {
      voiceAgent.sendContextualUpdate(`User attached images with the following descriptions:\n\n${analysisContext}`);
    }

    const dataUrls = await Promise.all(
      sentAttachments.map(
        (att) =>
          new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(att.file);
          }),
      ),
    );

    const imageMessage: TranscriptMessage = {
      message: "",
      isUser: true,
      timestamp: new Date(),
      imageAttachments: sentAttachments.map((att, index) => ({
        id: att.id,
        previewUrl: dataUrls[index] ?? att.previewUrl,
        analysisText: att.analysisText,
      })),
    };
    setMessages((prev) => [...prev, imageMessage]);

    if (!hasTypedText) {
      const promptText = analysisContext
        ? sentAttachments.length === 1
          ? "I've attached a photo."
          : "I've attached some photos."
        : "I attached photos for the team. Please ask me what details they should review.";
      attachmentPromptEchoesRef.current.push(promptText);
      voiceAgent.sendUserMessage(promptText);
    }

    stageImagesRef.current(
      sentAttachments.map((att) => ({
        id: att.id,
        file: att.file,
        analysisText: att.analysisText,
        compressedBase64: att.compressedBase64,
      })),
      voiceAgent.getConversationId(),
    );
    setSubmittedImageCountRef.current((count) => count + sentAttachments.length);
    sentAttachments.forEach((attachment) => {
      removeAttachmentRef.current(attachment.id);
      deliveringIdsRef.current.delete(attachment.id);
    });
  }

  /**
   * Mid-conversation, an image reaches the Genie as soon as its analysis
   * settles — a visitor talking by voice never presses send. The page calls
   * this whenever its attachments change, since they reach this hook as a ref.
   */
  function deliverPendingAttachments() {
    if (!voiceAgent.connected) return;
    const pending = attachmentsRef.current.filter((attachment) => !deliveringIdsRef.current.has(attachment.id));
    if (pending.length === 0 || pending.some((attachment) => attachment.status === "analyzing")) return;
    pending.forEach((attachment) => deliveringIdsRef.current.add(attachment.id));
    void deliverAttachments(pending, false);
  }

  // Welcome gate → voice. Dismiss the gate, remember it for this Genie so it's
  // first-use only, then run the normal start path.
  const handleStartVoiceFromWelcome = async () => {
    if (memoryConsentMutation.isPending) return;
    if (canManageCustomerMemory && agent?.memoryConsent === "unset") {
      await updateCustomerMemoryConsent(false);
    }
    dismissWelcome(agent?.id);
    void handleAvatarClick();
  };

  // Welcome gate → text chat. Mirrors handleInlineSend's text-start branch so a
  // visitor can land straight in text-only mode without a first message.
  const handleStartTextFromWelcome = async () => {
    if (memoryConsentMutation.isPending) return;
    if (canManageCustomerMemory && agent?.memoryConsent === "unset") {
      await updateCustomerMemoryConsent(false);
    }
    dismissWelcome(agent?.id);
    if (voiceAgent.connected || voiceAgent.isConnecting) return;

    const service = voiceAgent.getService();
    if (service) {
      service.setOverrides({
        conversation: { textOnly: true },
        turn: { turn_timeout: 600, silence_end_call_timeout: 600 },
      });
    }
    try {
      await voiceAgent.startConversation();
    } catch (error) {
      console.error("Error starting text conversation:", error);
      return;
    }
    setConversationStartTime(Date.now());
    setIsTextOnlyMode(true);
    setStartedFromText(true);
    setShowTranscript(true);
    isTextInitiatedRef.current = true;
    setMessages([]);
  };

  const handleToggleMicMute = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    const next = !isMicMuted;
    try {
      await service.setMicMuted(next);
      setIsMicMuted(next);
    } catch (error) {
      console.error("Error toggling microphone mute", error);
    }
  };

  const handleToggleAgentVoice = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      if (isAgentVoiceMuted) {
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        setIsAgentVoiceMuted(false);
      } else {
        await service.setVolume(0);
        setIsAgentVoiceMuted(true);
        if (isTranscriptEnabled()) {
          setShowTranscript(true);
        }
      }
    } catch (error) {
      console.error("Error toggling agent voice output", error);
    }
  };

  // Pause = phone-style hold. Mute mic + agent voice together, dump the
  // audio queue so the agent doesn't finish its sentence on resume, and
  // remember prior mute states so resume restores them exactly.
  const handleTogglePause = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    try {
      if (isPaused) {
        if (pauseActivityIntervalRef.current) {
          clearInterval(pauseActivityIntervalRef.current);
          pauseActivityIntervalRef.current = null;
        }
        service.sendContextualUpdate("The user has resumed the conversation.");
        // Flush anything the server queued up during the pause before we
        // bring the volume back, so resume starts from clean silence.
        service.interruptCurrentSpeech();
        await service.setMicMuted(prePauseMicMutedRef.current);
        await service.setVolume(prePauseAgentMutedRef.current ? 0 : DEFAULT_AGENT_VOICE_VOLUME);
        setIsMicMuted(prePauseMicMutedRef.current);
        setIsAgentVoiceMuted(prePauseAgentMutedRef.current);
        setIsPaused(false);
      } else {
        prePauseMicMutedRef.current = isMicMuted;
        prePauseAgentMutedRef.current = isAgentVoiceMuted;
        service.sendContextualUpdate(
          "The user has paused the conversation and will return shortly. Do not interpret the silence as disengagement — wait for them to resume.",
        );
        await service.setMicMuted(true);
        // setVolume(0) now hard-cuts gain and flushes the audio worklet
        // buffer, so the agent stops mid-sentence instead of playing out.
        await service.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
        setIsPaused(true);
        // Ping every 1.5s — sendUserActivity suppresses the agent's silence
        // timeout for ~2s per call, so this keeps it held indefinitely while paused.
        pauseActivityIntervalRef.current = setInterval(() => {
          service.sendUserActivity();
        }, 1500);
      }
    } catch (error) {
      console.error("Error toggling pause", error);
    }
  };

  const handleToggleAllAudioMute = async () => {
    const service = voiceAgent.getService();
    if (!service) return;
    const shouldUnmuteAll = isAllAudioMuted;
    try {
      if (shouldUnmuteAll) {
        await service.setMicMuted(false);
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        setIsMicMuted(false);
        setIsAgentVoiceMuted(false);
      } else {
        await service.setMicMuted(true);
        await service.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
      }
    } catch (error) {
      console.error("Error toggling all audio channels", error);
    }
  };

  const handleNoiseSwitchToText = async () => {
    setHasVoiceTrouble(false);
    setShowVoiceTrouble(false);
    setShowWrapUp(false);
    try {
      if (voiceAgent.connected) {
        // Keep the live conversation and its context; only change the interface.
        const service = voiceAgent.getService();
        await service?.setMicMuted(true);
        await service?.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
        setIsTextOnlyMode(true);
        setShowTranscript(true);
        return;
      }
      const service = voiceAgent.getService();
      if (service) {
        service.setOverrides({
          conversation: { textOnly: true },
          turn: {
            turn_timeout: 600,
            silence_end_call_timeout: 600,
          },
        });
      }
      const started = await voiceAgent.startConversation();
      if (started) {
        setIsTextOnlyMode(true);
        setStartedFromText(true);
        setShowTranscript(true);
        isTextInitiatedRef.current = true;
        setMessages([]);
      } else {
        toast.error("Could not switch to text chat. Please try again.");
      }
    } catch (e) {
      console.error("Error switching to text from noise hint", e);
      toast.error("Could not switch to text chat. Please try again.");
    }
  };

  const handleEndTextChat = async () => {
    try {
      const service = voiceAgent.getService();
      if (service) {
        await service.setVolume(DEFAULT_AGENT_VOICE_VOLUME);
        await service.setMicMuted(false);
      }
    } catch (e) {
      console.error("Error resetting voice output/mic before end chat", e);
    }
    try {
      await endConversationAfterUploadsRef.current();
    } catch (e) {
      console.error("Error ending text chat conversation", e);
    }
    setShowTranscript(false);
    setInlineMessage("");
    setStartedFromText(false);
    setIsTextOnlyMode(false);
    setIsMicMuted(false);
    setIsAgentVoiceMuted(false);
  };

  // Status message is handled by the hook — the page suppresses this during a
  // live human call since HumanCallBar shows its own status right below the avatar.
  const getStatusMessage = () => voiceAgent.getStatusMessage();

  return {
    voiceAgent,
    expectedEndRef,
    lastConversationId,
    setLastConversationId,
    showTranscript,
    setShowTranscript,
    chatExpanded,
    setChatExpanded,
    messages,
    setMessages,
    conversationStartTime,
    setConversationStartTime,
    inlineMessage,
    setInlineMessage,
    isTextOnlyMode,
    setIsTextOnlyMode,
    isMicMuted,
    isAgentVoiceMuted,
    isPaused,
    startedFromText,
    setStartedFromText,
    isAllAudioMuted,
    shouldUseWrapUp,
    isTranscriptEnabled,
    handleInlineSend,
    deliverPendingAttachments,
    handleAvatarClick,
    handleStartVoiceFromWelcome,
    handleStartTextFromWelcome,
    handleToggleMicMute,
    handleToggleAgentVoice,
    handleTogglePause,
    handleToggleAllAudioMute,
    handleNoiseSwitchToText,
    handleEndTextChat,
    handleStartOver,
    getStatusMessage,
  };
}
